import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, type AdminUser } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction, BulkAction } from "@/components/ui";
import {
  Trash2,
  RotateCcw,
  Edit,
  ShieldCheck,
  User as UserIcon,
  Download,
  KeyRound,
  UserCheck,
  Clock,
} from "lucide-react";
import { AdminUserDetailModal } from "./AdminUserDetailModal";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const ROLE_OPTIONS = [
  { value: "", label: "Alla roller" },
  { value: "super_admin", label: "Super Admin" },
  { value: "user", label: "Användare" },
];

const VERIFIED_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "true", label: "Ja" },
  { value: "false", label: "Nej" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiva" },
  { value: "all", label: "Alla" },
];

const getRoleIcon = (role: string) => {
  switch (role) {
    case "super_admin":
      return <ShieldCheck size={14} className="text-red-500" />;
    default:
      return <UserIcon size={14} className="text-gray-400" />;
  }
};

const roleBadgeStyles: Record<string, string> = {
  super_admin: "bg-red-100 text-red-700",
  user: "bg-gray-100 text-gray-700",
};

const roleLabels: Record<string, string> = {
  super_admin: "Super Admin",
  user: "Användare",
};

// Default retention period (can be changed in admin settings)
const DEFAULT_RETENTION_DAYS = 30;

/**
 * Calculate days remaining until permanent deletion
 */
function getDaysUntilPermanentDelete(deletedAt: string | null | undefined): number | null {
  if (!deletedAt) return null;
  const deletedDate = new Date(deletedAt);
  const permanentDeleteDate = new Date(deletedDate);
  permanentDeleteDate.setDate(permanentDeleteDate.getDate() + DEFAULT_RETENTION_DAYS);
  const now = new Date();
  const diffMs = permanentDeleteDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

export function AdminUsers() {
  const navigate = useNavigate();
  const { user: currentUser, startImpersonation } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminUser, { status: string; role: string; emailVerified: string; createdAt: string }>({
    fetcher: (params) =>
      adminApi.listUsers({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        includeDeleted: params.status === "all" || undefined,
        role: params.role || undefined,
        emailVerified: params.emailVerified || undefined,
        createdAfter: params.createdAt || undefined,
        createdBefore: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "active", role: "", emailVerified: "", createdAt: "" },
    sortKeyMap: {
      user: "email",
      role: "role",
      emailVerified: "emailVerified",
      createdAt: "createdAt",
    },
  });

  const handleDelete = useCallback(
    async (user: AdminUser) => {
      if (user.id === currentUser?.id) return;
      if (!confirm("Är du säker på att du vill ta bort denna användare?"))
        return;
      try {
        const response = await adminApi.deleteUser(user.id);
        if (response.success) {
          refresh();
          toast.success("Användaren har tagits bort");
        }
      } catch {
        toast.error("Kunde inte ta bort användaren");
      }
    },
    [currentUser?.id, refresh]
  );

  const handleRestore = useCallback(
    async (user: AdminUser) => {
      try {
        const response = await adminApi.restoreUser(user.id);
        if (response.success) {
          refresh();
          toast.success("Användaren har återställts");
        }
      } catch {
        toast.error("Kunde inte återställa användaren");
      }
    },
    [refresh]
  );

  const handleImpersonate = useCallback(
    async (user: AdminUser) => {
      if (!confirm(`Är du säker på att du vill logga in som ${user.email}?`)) {
        return;
      }
      try {
        const response = await adminApi.impersonateUser(user.id);
        if (response.success && response.data) {
          startImpersonation(response.data);
          toast.success(`Du är nu inloggad som ${response.data.user.email}`);
          navigate("/dashboard");
        } else {
          toast.error(response.error?.message || "Kunde inte starta impersonation");
        }
      } catch {
        toast.error("Kunde inte ansluta till servern");
      }
    },
    [navigate, startImpersonation]
  );

  const columns: ColumnDef<AdminUser>[] = useMemo(
    () => [
      {
        id: "user",
        header: "Användare",
        sortable: true,
        sortAccessor: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim(),
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök namn eller e-post...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">
              {row.firstName} {row.lastName}
            </p>
            <p className="text-xs text-gray-500">{row.email}</p>
          </div>
        ),
      },
      {
        id: "role",
        header: "Roll",
        sortable: true,
        sortAccessor: (row) => row.role,
        filter: {
          type: "select" as const,
          value: filters.role,
          onChange: (v: string) => setFilter("role", v),
          options: ROLE_OPTIONS,
        },
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium w-fit ${roleBadgeStyles[row.role] || roleBadgeStyles.user}`}
            >
              {getRoleIcon(row.role)}
              {roleLabels[row.role] || row.role}
            </span>
            {row.adminRoleNames.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {row.adminRoleNames.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200/60"
                  >
                    <KeyRound size={10} />
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "emailVerified",
        header: "Verifierad",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => row.emailVerified,
        filter: {
          type: "select" as const,
          value: filters.emailVerified,
          onChange: (v: string) => setFilter("emailVerified", v),
          options: VERIFIED_OPTIONS,
        },
        cell: (row) =>
          row.emailVerified ? (
            <span className="text-xs font-medium text-green-600">Ja</span>
          ) : (
            <span className="text-xs font-medium text-gray-400">Nej</span>
          ),
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => (row.deletedAt ? 1 : 0),
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) => {
          if (row.deletedAt) {
            const daysRemaining = getDaysUntilPermanentDelete(row.deletedAt);
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <Trash2 size={12} />
                  Borttagen
                </span>
                {daysRemaining !== null && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-red-600">
                    <Clock size={10} />
                    {daysRemaining === 0
                      ? "Raderas snart"
                      : daysRemaining === 1
                        ? "Raderas om 1 dag"
                        : `Raderas om ${daysRemaining} dagar`}
                  </span>
                )}
              </div>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Aktiv
            </span>
          );
        },
      },
    ],
    [search, setSearch, filters.role, filters.emailVerified, filters.createdAt, filters.status, setFilter]
  );

  const isSuperAdmin = currentUser?.role === "super_admin";

  const rowActions: RowAction<AdminUser>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Visa / Redigera",
        icon: Edit,
        onClick: (row) => setSelectedUserId(row.id),
      },
      {
        key: "impersonate",
        label: "Logga in som",
        icon: UserCheck,
        onClick: handleImpersonate,
        visible: (row) => isSuperAdmin && !row.deletedAt && row.id !== currentUser?.id,
      },
      {
        key: "restore",
        label: "Återställ",
        icon: RotateCcw,
        variant: "success",
        onClick: handleRestore,
        visible: (row) => !!row.deletedAt,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleDelete,
        visible: (row) => !row.deletedAt && row.id !== currentUser?.id,
      },
    ],
    [currentUser?.id, isSuperAdmin, handleDelete, handleImpersonate, handleRestore]
  );

  // Bulk actions
  const handleBulkDelete = useCallback(
    async (users: AdminUser[]) => {
      const deletableUsers = users.filter(
        (u) => !u.deletedAt && u.id !== currentUser?.id
      );
      if (deletableUsers.length === 0) {
        toast.error("Inga användare kan tas bort");
        return;
      }
      if (
        !confirm(
          `Är du säker på att du vill ta bort ${deletableUsers.length} användare?`
        )
      ) {
        return;
      }
      setIsBulkDeleting(true);
      try {
        let successCount = 0;
        let errorCount = 0;
        for (const user of deletableUsers) {
          try {
            const response = await adminApi.deleteUser(user.id);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch {
            errorCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} användare har tagits bort`);
          setSelectedKeys(new Set());
          refresh();
        }
        if (errorCount > 0) {
          toast.error(`Kunde inte ta bort ${errorCount} användare`);
        }
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [currentUser?.id, refresh]
  );

  const handleBulkExport = useCallback((users: AdminUser[]) => {
    downloadCsv(
      users,
      [
        { header: "ID", accessor: (r) => r.id },
        { header: "Förnamn", accessor: (r) => r.firstName },
        { header: "Efternamn", accessor: (r) => r.lastName },
        { header: "E-post", accessor: (r) => r.email },
        { header: "Roll", accessor: (r) => r.role },
        { header: "Adminroller", accessor: (r) => r.adminRoleNames.join(", ") },
        { header: "Verifierad", accessor: (r) => (r.emailVerified ? "Ja" : "Nej") },
        { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
        { header: "Status", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
      ],
      `anvandare-urval-${new Date().toISOString().slice(0, 10)}.csv`
    );
    toast.success(`${users.length} användare exporterade`);
  }, []);

  const bulkActions: BulkAction<AdminUser>[] = useMemo(
    () => [
      {
        key: "export",
        label: "Exportera",
        icon: Download,
        onClick: handleBulkExport,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleBulkDelete,
        loading: isBulkDeleting,
      },
    ],
    [handleBulkExport, handleBulkDelete, isBulkDeleting]
  );

  // Can't select current user or already deleted users for deletion
  const canSelectUser = useCallback(
    (user: AdminUser) => {
      return user.id !== currentUser?.id;
    },
    [currentUser?.id]
  );

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.users.title} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Användare</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Hantera alla registrerade användare
            </p>
          </div>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          size="sm"
          onClick={() =>
            downloadCsv(
              data,
              [
                { header: "ID", accessor: (r) => r.id },
                { header: "Förnamn", accessor: (r) => r.firstName },
                { header: "Efternamn", accessor: (r) => r.lastName },
                { header: "E-post", accessor: (r) => r.email },
                { header: "Roll", accessor: (r) => r.role },
                { header: "Adminroller", accessor: (r) => r.adminRoleNames.join(", ") },
                { header: "Verifierad", accessor: (r) => (r.emailVerified ? "Ja" : "Nej") },
                { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
                { header: "Status", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
              ],
              `anvandare-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }
          disabled={data.length === 0}
        >
          <Download size={14} />
          Exportera CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga användare hittades"
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100, 250]}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setSelectedUserId(row.id)}
        rowActions={rowActions}
        rowClassName={(row) => (row.deletedAt ? "bg-red-50/50 hover:bg-red-100/50" : undefined)}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        bulkActions={bulkActions}
        canSelect={canSelectUser}
      />

      {selectedUserId && (
        <AdminUserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUpdated={refresh}
        />
      )}
    </div>
    </>
  );
}
